import { MessageStatus } from './useSearchTickets.interface';
import { filterTickets } from './requestedTickets';

import { sendDatadogRumError } from 'utils/analytics/datadogRum';
import zendeskGateway from 'services/help-center/zendeskBackendGateway';
import { ApiResponse } from 'modules/IMC/MessageCenter/components/MessageCenterContent/MessageList/MessageList.interface';
import { OPEN_MESSAGES_STATUSES } from 'services/help-center/constants';
import { sortByLastPublicComment } from 'services/help-center/recentlyCreatedTickets';

/**
 * If searchQuery is provided:
 *   - Use `/search.json` with `include=tickets(last_comment)` to fetch filtered tickets with last_comment included.
 * If no searchQuery:
 *   - Use `/users/{zdUserId}/tickets/requested` with `include=last_comment` to fetch all requested tickets with last_comment included,
 *     then filter them by status if provided.
 */
export default async function fetchTickets(
    zdUserId?: number,
    searchQuery?: string,
    status?: MessageStatus[]
): Promise<ApiResponse> {
    if (!zdUserId) {
        return { results: [], count: 0, facets: {}, next_page: null, previous_page: null };
    }

    try {
        const isOpenMessagesStatus = status?.every((s) => OPEN_MESSAGES_STATUSES.includes(s));

        if (searchQuery) {
            // Search scenario
            const statusQuery = `${status?.map((s) => `status:${s}`).join(' ')} status:solved`; // Always include solved tickets to get recently solved tickets

            const query = [statusQuery, searchQuery?.trim(), `type:ticket requester:${zdUserId}`]
                .filter(Boolean)
                .join(' ');

            const res = await zendeskGateway.get('/search.json', {
                params: {
                    query,
                    include: 'tickets(last_comment)',
                    sort_by: 'created_at',
                    sort_order: 'desc',
                    size: 20,
                },
            });

            const filteredResults = filterTickets(res.data.results, isOpenMessagesStatus).sort(sortByLastPublicComment);

            return { ...res.data, results: filteredResults };
        }

        // No searchQuery: fetch requested tickets
        const requestedRes = await zendeskGateway.get(`/users/${zdUserId}/tickets/requested`, {
            params: {
                include: 'last_comment',
                sort_by: 'created_at',
                sort_order: 'desc',
                size: 20,
            },
        });

        const filteredTickets = filterTickets(requestedRes.data.tickets || [], isOpenMessagesStatus).sort(
            sortByLastPublicComment
        );

        return {
            results: filteredTickets,
            count: filteredTickets.length,
            facets: {},
            next_page: null,
            previous_page: null,
        };
    } catch (error) {
        sendDatadogRumError('fetchTickets', error, { source: 'fetchTickets', zdUserId, searchQuery, status });
        throw new Error('Failed to fetch tickets. Please try again later.');
    }
}
